import { useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useUserActivityDetail } from '../../queries/analytics';
import { shiftDateIso, todayLocalIso } from '../../lib/date';
import { actionBreakdown, activityDetail, eventLabel } from '../../lib/activity';

const EVENT_COLORS: Record<string, string> = {
  page_view: '#3B82F6',
  login_success: '#10B981',
  login_attempt: '#6B7280',
  content_expand: '#F59E0B',
  content_collapse: '#9CA3AF',
  registration_success: '#8B5CF6',
  // the new look (_v2): lighter shades of the classic colours, plus its own toggles and clicks
  page_view_v2: '#93C5FD',
  content_expand_v2: '#FCD34D',
  content_collapse_v2: '#D1D5DB',
  ui_toggle_v2: '#F472B6',
  ui_click_v2: '#2DD4BF',
  ui_version_switch: '#A78BFA',
};
const DEFAULT_COLOR = '#94A3B8';

const TIMEFRAMES = [
  { value: '15', label: '15 minutes' },
  { value: '30', label: '30 minutes' },
  { value: '60', label: '1 hour' },
  { value: '120', label: '2 hours' },
  { value: '240', label: '4 hours' },
];

interface Props {
  userIdentifier: string;
  initialDate?: string | null;
  initialTimeframe?: string;
}

/** Self-contained per-user activity view: its own timeframe/date selector, the stacked event-type chart, By Event Type / By Page breakdowns, and a timeline. Shared by the dashboard's user modal and the user-detail Activity tab. */
export function UserActivityPanel({ userIdentifier, initialDate = null, initialTimeframe = '240' }: Props) {
  const [timeframe, setTimeframe] = useState(initialTimeframe);
  const [date, setDate] = useState<string | null>(initialDate);
  const { data, isLoading } = useUserActivityDetail(userIdentifier, { date, timeframe });

  const { buckets, eventTypes } = useMemo(() => {
    const types = new Set<string>();
    const byBucket = new Map<string, Record<string, number | string>>();
    for (const a of data?.activities ?? []) {
      if (!a.timestamp) continue;
      types.add(a.event_type);
      const label = new Date(a.timestamp).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', hour12: false });
      const bucket = byBucket.get(label) ?? { label };
      bucket[a.event_type] = (Number(bucket[a.event_type]) || 0) + 1;
      byBucket.set(label, bucket);
    }
    return { buckets: Array.from(byBucket.values()).reverse(), eventTypes: Array.from(types) };
  }, [data]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">{data ? `Activity for ${data.summary.timeRange}` : '…'}</p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={date ? 'today' : timeframe}
            onChange={(e) => {
              if (e.target.value === 'today') {
                setDate(todayLocalIso());
              } else {
                setDate(null);
                setTimeframe(e.target.value);
              }
            }}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          >
            {TIMEFRAMES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
            <option value="today">Today</option>
          </select>
          {date && (
            <button
              type="button"
              onClick={() => setDate(shiftDateIso(date, -1))}
              aria-label="Previous day"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          )}
          <input
            type="date"
            value={date ?? ''}
            onChange={(e) => setDate(e.target.value || null)}
            className="rounded-md border border-input bg-background px-2 py-1.5 text-sm"
          />
          {date && (
            <button
              type="button"
              onClick={() => setDate(shiftDateIso(date, 1))}
              disabled={date >= todayLocalIso()}
              aria-label="Next day"
              className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-input text-muted-foreground hover:bg-accent hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : !data ? (
        <p className="py-12 text-center text-sm text-muted-foreground">No activity found.</p>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Tile label="Total Activities" value={data.summary.totalActivities} />
            <Tile label="Event Types" value={data.summary.uniqueEventTypes} />
            <Tile label="Unique Pages" value={data.summary.uniquePages} />
          </div>

          {buckets.length > 0 && (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={buckets}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e5e7eb)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  {eventTypes.map((t) => (
                    <Bar key={t} dataKey={t} stackId="a" fill={EVENT_COLORS[t] || DEFAULT_COLOR} name={t.replace(/_/g, ' ')} />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <Breakdown title="By Event Type" rows={data.breakdown.eventTypes.map((e) => ({ label: eventLabel(e.type), count: e.count }))} />
            <Breakdown title="By Action" rows={actionBreakdown(data.activities)} />
            <Breakdown title="By Page" rows={data.breakdown.pageTypes.map((p) => ({ label: p.type ?? 'unknown', count: p.count }))} />
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">Timeline</p>
            <div className="space-y-1">
              {data.activities.length === 0 && <p className="text-xs text-muted-foreground">No activity in this window.</p>}
              {data.activities.slice(0, 100).map((a, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span className="flex items-center gap-2">
                    <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: EVENT_COLORS[a.event_type] || DEFAULT_COLOR }} />
                    <span className="rounded bg-muted px-1.5 py-0.5 font-medium">{eventLabel(a.event_type)}</span>
                    {activityDetail(a.metadata) && <span className="font-medium text-foreground">{activityDetail(a.metadata)}</span>}
                    {a.page_type && <span className="text-muted-foreground">{a.page_type}</span>}
                    {a.track_name && (
                      <span className="text-muted-foreground">
                        Track: <span className="font-medium text-foreground">{a.track_name}</span>
                        {a.race_number ? ` R${a.race_number}` : ''}
                      </span>
                    )}
                  </span>
                  <span className="text-muted-foreground">{a.timestamp ? new Date(a.timestamp).toLocaleString('en-AU', { hour12: false }) : ''}</span>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Tile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-muted/40 p-3 text-center">
      <div className="text-xl font-bold">{value.toLocaleString()}</div>
      <div className="text-xs text-muted-foreground">{label}</div>
    </div>
  );
}

function Breakdown({ title, rows }: { title: string; rows: { label: string; count: number }[] }) {
  return (
    <div>
      <p className="mb-2 text-sm font-medium">{title}</p>
      <div className="space-y-1">
        {rows.length === 0 && <p className="text-xs text-muted-foreground">None.</p>}
        {rows.map((r) => (
          <div key={r.label} className="flex items-center justify-between text-xs">
            <span>{r.label}</span>
            <span className="font-medium">{r.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
